import React from "react";
import clsx from "clsx";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../../../../ui/index.ts";
import styles from "./withdrawn-status-view.module.css";

interface WithdrawnStatusViewProps {
    withdrawalReason?: string | null;
    withdrawnDate?: string;
    loading?: boolean;
    onResign: () => void;
    updatedAt?: string | null;
}

const FRIDAY = 5;
const TUESDAY = 2;

function getWithdrawnProceedAt(updatedAt: string | null | undefined): number | null {
    if (!updatedAt) return null;

    const updatedDate = new Date(updatedAt);
    const updatedTime = updatedDate.getTime();
    if (Number.isNaN(updatedTime)) return null;

    if (updatedDate.getDay() === FRIDAY) {
        const proceedDate = new Date(updatedDate);
        const daysUntilTuesday = (7 - FRIDAY) + TUESDAY;
        proceedDate.setDate(proceedDate.getDate() + daysUntilTuesday);
        return proceedDate.getTime();
    }

    const proceedDate = new Date(updatedDate);
    proceedDate.setDate(proceedDate.getDate() + 2);
    proceedDate.setHours(0, 0, 0, 0);
    return proceedDate.getTime();
}

export function WithdrawnStatusView({
    withdrawalReason,
    withdrawnDate,
    loading = false,
    onResign,
    updatedAt,
}: WithdrawnStatusViewProps) {
    const [now, setNow] = React.useState(() => Date.now());
    const proceedAt = React.useMemo(() => getWithdrawnProceedAt(updatedAt), [updatedAt]);
    const showProceedButton = proceedAt != null && now >= proceedAt;
    const trimmedReason = withdrawalReason?.trim();
    const formattedDate = withdrawnDate ? new Date(withdrawnDate).toLocaleDateString() : "N/A";

    React.useEffect(() => {
        setNow(Date.now());
    }, [proceedAt]);

    React.useEffect(() => {
        if (proceedAt == null || now >= proceedAt) return;

        const delay = Math.max(proceedAt - now, 0);
        const timer = window.setTimeout(() => setNow(Date.now()), delay);

        return () => window.clearTimeout(timer);
    }, [now, proceedAt]);

    return (
      <div className={styles.content}>
        <div className={styles.iconWrap} aria-hidden>
          <CheckCircle2 className={styles.icon} />
        </div>
        <h2 className={clsx("heading-lg", styles.title)}>
          Offboarding Request Withdrawn
        </h2>
        <p className={clsx("sub-heading-md", styles.message)}>
          Your offboarding request has been withdrawn on {formattedDate}. No further
          action is required at this time.
        </p>
        {trimmedReason && (
          <div className={styles.reasonPanel}> 
            <p className={clsx("sub-heading-md", styles.reasonLabel)}>
              Reason for withdrawal
            </p>
            <p className={clsx("body-sm", styles.reasonText)}>
              {trimmedReason}
            </p>
          </div>
        )}
        {showProceedButton ? (
          <Button variant="danger-ghost" onClick={onResign} disabled={loading}>
            Proceed with Offboarding
            <ArrowRight size={16} />
          </Button>
        ) : proceedAt != null ? (
          <p className={clsx("body-sm", styles.hint)}>
            You may access offboarding request form again after the completion of the next working day.
          </p>
        ) : null}
      </div>
    );
}
